import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Save, Plus, Trash2, Loader2, ExternalLink, Calendar, Eye, EyeOff, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminPremsa = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  
  // New link form
  const [newUrl, setNewUrl] = useState('');
  const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0]);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/press`, {
        
      });
      if (response.ok) {
        const data = await response.json();
        setLinks(data.links || []);
      }
    } catch (error) {
      console.error('Error fetching press links:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async () => {
    if (!newUrl) return;
    
    setLoadingPreview(true);
    setPreview(null);
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/press/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify({ url: newUrl })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreview(data.metadata);
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleAddLink = async () => {
    if (!newUrl || !newDate) {
      setMessage({ type: 'error', text: 'URL i data són obligatoris' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    setSaving(true);
    try {
      // Include preview metadata if available
      const linkData = { 
        url: newUrl, 
        date: newDate,
        ...(preview && {
          title: preview.title,
          description: preview.description,
          image: preview.image,
          site_name: preview.site_name
        })
      };
      
      const response = await authFetch(`${API_URL}/api/admin/press`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify(linkData)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Enllaç afegit correctament!' });
        setNewUrl('');
        setNewDate(new Date().toISOString().split('T')[0]);
        setPreview(null);
        fetchLinks();
      } else {
        const errorData = await response.text();
        console.error('Error response:', response.status, errorData);
        setMessage({ type: 'error', text: `Error afegint l'enllaç (${response.status})` });
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setMessage({ type: 'error', text: 'Error de connexió' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const toggleActive = async (linkId, isActive) => {
    try {
      await authFetch(`${API_URL}/api/admin/press/${linkId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify({ is_active: !isActive })
      });
      fetchLinks();
    } catch (error) {
      console.error('Error toggling link:', error);
    }
  };

  const deleteLink = async (linkId) => {
    if (!window.confirm('Segur que vols eliminar aquest enllaç?')) return;
    
    try {
      await authFetch(`${API_URL}/api/admin/press/${linkId}`, {
        method: 'DELETE',
        
      });
      fetchLinks();
      setMessage({ type: 'success', text: 'Enllaç eliminat' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting link:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ca-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto" data-testid="admin-premsa">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Premsa</h1>
          <p className="text-gray-600 text-sm mt-1">Gestiona els enllaços a mencions en premsa i xarxes</p>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {message.text}
          </div>
        )}

        {/* Add new link form */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <h2 className="font-semibold text-gray-900 mb-4">Afegir nou enllaç</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  onBlur={fetchPreview}
                  placeholder="https://..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                  data-testid="new-url-input"
                />
                <button
                  onClick={fetchPreview}
                  disabled={loadingPreview || !newUrl}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  title="Actualitzar previsualització"
                >
                  {loadingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                data-testid="new-date-input"
              />
            </div>
          </div>

          {/* Preview */}
          {loadingPreview && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-[#1A4D2E] mr-2" />
              <span className="text-sm text-gray-600">Carregant previsualització...</span>
            </div>
          )}
          
          {preview && !loadingPreview && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
              <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Previsualització</p>
              <div className="flex gap-4">
                {preview.image && (
                  <div className="w-32 h-24 flex-shrink-0 rounded overflow-hidden bg-gray-200">
                    <img src={preview.image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {preview.site_name && (
                    <p className="text-xs text-[#1A4D2E] font-medium mb-1">{preview.site_name}</p>
                  )}
                  {preview.title && (
                    <h4 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1">{preview.title}</h4>
                  )}
                  {preview.description && (
                    <p className="text-xs text-gray-600 line-clamp-2">{preview.description}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleAddLink}
            disabled={saving || !newUrl || !newDate}
            className="flex items-center gap-2 bg-[#1A4D2E] text-white px-6 py-2.5 rounded-lg hover:bg-[#153d24] transition-colors disabled:opacity-50"
            data-testid="add-link-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Afegir enllaç
          </button>
        </div>

        {/* Links list */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Enllaços ({links.length})</h2>
          </div>
          
          {links.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No hi ha enllaços de premsa
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {links.map((link) => (
                <div 
                  key={link.id} 
                  className={`p-4 ${!link.is_active ? 'bg-gray-50 opacity-60' : ''}`}
                  data-testid={`link-item-${link.id}`}
                >
                  <div className="flex gap-4">
                    {/* Image thumbnail */}
                    {link.image && (
                      <div className="w-24 h-16 flex-shrink-0 rounded overflow-hidden bg-gray-200">
                        <img src={link.image} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(link.date)}
                        {link.site_name && (
                          <>
                            <span>•</span>
                            <span className="text-[#1A4D2E] font-medium">{link.site_name}</span>
                          </>
                        )}
                      </div>
                      
                      {link.title ? (
                        <h4 className="font-medium text-gray-900 text-sm line-clamp-1 mb-1">{link.title}</h4>
                      ) : (
                        <p className="text-sm text-gray-500 truncate mb-1">{link.url}</p>
                      )}
                      
                      {link.description && (
                        <p className="text-xs text-gray-600 line-clamp-1">{link.description}</p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-500 hover:text-[#1A4D2E] hover:bg-gray-100 rounded-lg transition-colors"
                        title="Obrir enllaç"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => toggleActive(link.id, link.is_active)}
                        className={`p-2 rounded-lg transition-colors ${
                          link.is_active 
                            ? 'text-green-600 hover:bg-green-50' 
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={link.is_active ? 'Desactivar' : 'Activar'}
                      >
                        {link.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => deleteLink(link.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPremsa;
